import {orderStatusMapping, SFCC_ORDER_STATUS} from '../constant'
import {BRAND_KEY} from '../../../brand'

export const getInfoLabelVariantByOmsStatus = (omsStatus) => {
    if (typeof omsStatus !== 'string') return ''

    const statusMapping = {
        'partially delivered': BRAND_KEY === 'kenwood' ? 'coupon' : 'light',
        'partially shipped': BRAND_KEY === 'kenwood' ? 'coupon' : 'light',
        'return requested': BRAND_KEY === 'kenwood' ? 'violet' : 'light',
        active: 'coupon',
        'vat check': '',
        delivered: 'coupon',
        shipped: 'coupon',
        processing: BRAND_KEY === 'braun' ? 'light' : '',
        canceled: 'grey',
        'export Error': 'error',
        'non-conform': 'error',
        declined: 'error',
        expired: 'error',
        rejected: 'error',
        completed: 'coupon',
        failed: 'grey',
        dismissed: 'grey',
    }

    return statusMapping[omsStatus]
}

/**
 * Determines whether the "Edit" or "Cancel" actions should be hidden on subscriptions based on its status.
 *
 * @param {string} status - The current status of the order.
 * @returns {boolean} Returns true if the status is 'canceled', 'expired', or 'dismissed'; otherwise, false.
 */
export const isEditOrCancelHidden = (status) => {
    const hiddenStatuses = ['canceled', 'expired', 'dismissed']
    return hiddenStatuses.includes((status || '').toLowerCase())
}

// const getOrderFlow = (order, isReturn = false) => {
//     //add flow checks
//     // if (order?.OrderPaymentSummaries?.records?.[0]?.Refunds) return 'return'
//     // if (order?.referenceId === 'refSubscription' && order?.Status__c === 'Canceled')
//     //     return 'subscriptionCancelation'
//     // if (order?.fulfillmentOrder) return 'fulfillment'

//     // if (order?.returnOrder?.length > 0) return 'return'
//     if (!isReturn) return 'simple'
//     if (isReturn) return 'return'
// }

const getDefaultMessage = () => ({
    defaultMessage: 'Not found',
    id: 'not_found',
})

const getDefaultStatus = () => ({
    message: getDefaultMessage(),
    status: 'Not found',
})

const mapOmsToSfccStatus = (omsStatus) => {
    for (const flow in orderStatusMapping) {
        const mapping = orderStatusMapping[flow]?.find((m) =>
            m?.oms?.map((s) => s?.toLowerCase()?.trim()).includes(omsStatus?.toLowerCase()?.trim()),
        )
        if (mapping) {
            return mapping?.sfcc
        }
    }
    return null
}

/**
 * Determines the order status based on the table logic when there are multiple shipments.
 *
 * @param {string} orderStatus - The overall order status.
 * @param {Array<string>} shipmentStatuses - The statuses of individual shipments.
 * @returns {string} The calculated order status.
 */

const calculateOrderStatusForMultipleShipments = (orderStatus, shipmentStatuses) => {
    const normalizedStatuses = shipmentStatuses?.map((s) => mapOmsToSfccStatus(s))?.filter(Boolean)
    if (
        normalizedStatuses.length > 0 &&
        normalizedStatuses.every((status) => status === SFCC_ORDER_STATUS.DELIVERED)
    ) {
        return SFCC_ORDER_STATUS.DELIVERED
    }

    if (
        normalizedStatuses.length > 0 &&
        normalizedStatuses.every((status) => status === SFCC_ORDER_STATUS.SHIPPED)
    ) {
        return SFCC_ORDER_STATUS.SHIPPED
    }

    if (
        normalizedStatuses.length > 0 &&
        normalizedStatuses.every((status) => status === SFCC_ORDER_STATUS.PROCESSING)
    ) {
        return SFCC_ORDER_STATUS.PROCESSING
    }

    if (
        normalizedStatuses.includes(SFCC_ORDER_STATUS.DELIVERED) &&
        normalizedStatuses.some((status) => status !== SFCC_ORDER_STATUS.DELIVERED)
    ) {
        return SFCC_ORDER_STATUS.PARTIALLY_DELIVERED
    }

    if (
        normalizedStatuses.includes(SFCC_ORDER_STATUS.SHIPPED) &&
        normalizedStatuses.some((status) => status !== SFCC_ORDER_STATUS.SHIPPED)
    ) {
        return SFCC_ORDER_STATUS.PARTIALLY_SHIPPED
    }

    return mapOmsToSfccStatus(orderStatus?.toLowerCase())
}

/**
 *
 * @param {status} status
 * @param {isReturnFlow} isReturn
 * @returns {Object} An object containing a message and a status:
 *
 * Example:
 *
 * - message: {id: 'status.cancellation', defaultMessage: 'Cancellation'}
 * - status: 'Cancellation'
 *
 */
export const getOrderStatus = (
    status,
    isReturnFlow = false,
    shipmentStatuses = [],
    options = {},
) => {
    if (!status || typeof status !== 'string') return getDefaultStatus()

    const normalizedStatus = status?.toLowerCase()?.trim()
    const {hasUnassignedOrderItems = false} = options

    // Handle multiple shipments logic
    if (shipmentStatuses.length > 0) {
        const enrichedShipmentStatuses = hasUnassignedOrderItems
            ? [...shipmentStatuses, SFCC_ORDER_STATUS.IN_PREPARATION]
            : shipmentStatuses

        const calculatedStatus = calculateOrderStatusForMultipleShipments(
            normalizedStatus,
            enrichedShipmentStatuses,
        )
        return {
            message: {id: `status.${calculatedStatus}`, defaultMessage: calculatedStatus},
            status: calculatedStatus,
        }
    }

    const orderFlow = isReturnFlow ? 'return' : 'simple'

    if (!orderStatusMapping[orderFlow]) return getDefaultStatus()

    const statusMappings = orderStatusMapping[orderFlow] || []
    const mapping = statusMappings.find((m) =>
        m.oms.map((s) => s.toLowerCase().trim()).includes(normalizedStatus),
    )
    return {
        message: mapping?.message ?? getDefaultMessage(),
        status: mapping?.sfcc ?? 'Not found',
    }
}

export const isReturnEligible = (deliveryDate) => {
    if (!deliveryDate) return

    const deliveredDate = new Date(deliveryDate)
    const today = new Date()
    const cutoffDate = new Date(deliveredDate)

    cutoffDate.setDate(deliveredDate.getDate() + 30)

    return today <= cutoffDate
}

/**
 * Determines if an order has been exported based on its status.
 *
 * @param {string} orderStatus - The status of the order.
 * @returns {boolean} Returns `true` if the order is exported, otherwise `false`.
 */
export const isOrderExported = (orderStatus) => {
    if (
        orderStatus &&
        (orderStatus?.toLowerCase() === 'created' ||
            orderStatus?.toLowerCase() === 'grace period' ||
            orderStatus?.toLowerCase() === 'export error')
    ) {
        return false
    }
    return true
}

/**
 * Calculates the item's price based on its status.
 *
 * @param {string} status - The status to check.
 * @param {object} item - The item object containing price details.
 * @returns {number} Returns the calculated price based on the status.
 */
export const getItemPriceByStatus = (status, item) => {
    if (status?.toLowerCase() === 'canceled') {
        return item?.OriginalOrderItem?.TotalAmtWithTax / item?.QuantityOrdered
    }
    return item?.TotalLineAmountWithTax / item?.Quantity
}

export const splitTextIntoLines = (text, columnWidth, fontSize) => {
    const maxCharsPerLine = Math.floor(columnWidth / (fontSize * 0.6))
    const words = text.split(' ')
    const lines = []
    let currentLine = ''

    words.forEach((word) => {
        if ((currentLine + word).length <= maxCharsPerLine) {
            currentLine += `${word} `
        } else {
            lines.push(currentLine.trim())
            currentLine = `${word} `
        }
    })

    if (currentLine.trim()) {
        lines.push(currentLine.trim())
    }

    return lines
}

/**
 * Determines if a subscription can be edited or canceled based on the next renewal date.
 * Allows editing/canceling if the next renewal date is more than 2 days away.
 * @param {object} subscription - The subscription object (must have renewal.nextDate).
 * @returns {boolean} True if editable/cancellable, false otherwise.
 */
export const canEditOrCancelSubscription = (subscription) => {
    if (!subscription?.renewal?.nextDate) return true
    const nextDate = new Date(subscription.renewal.nextDate)
    const now = new Date()
    const diffMs = nextDate - now
    const diffDays = diffMs / (1000 * 60 * 60 * 24)
    return diffDays > 2
}

/**
 * Sorts order items, placing warranty items after their associated products.
 * @param {Array} items - The array of order items.
 * @returns {Array} The sorted array of order items.
 */

export const sortOrderItemsWithWarranty = (items = []) => {
    const orderedItems = []
    const processedIds = new Set()

    items.forEach((item) => {
        if (item?.Id && processedIds.has(item.Id)) {
            return
        }

        const warrantyUUID = item?.UUIDWarranty__c

        if (warrantyUUID) {
            const group = items.filter((i) => i?.UUIDWarranty__c === warrantyUUID)

            const sortedGroup = [...group].sort((a, b) => {
                const aIsWarranty = a?.PurchaseType__c === 'Warranty'
                const bIsWarranty = b?.PurchaseType__c === 'Warranty'

                if (!aIsWarranty && bIsWarranty) {
                    return -1
                }

                if (aIsWarranty && !bIsWarranty) {
                    return 1
                }

                return 0
            })

            sortedGroup.forEach((groupedItem) => {
                orderedItems.push(groupedItem)

                if (groupedItem?.Id) {
                    processedIds.add(groupedItem.Id)
                }
            })
        } else {
            orderedItems.push(item)

            if (item?.Id) {
                processedIds.add(item.Id)
            }
        }
    })

    return orderedItems
}

const normalizePaymentMethodCode = (value) => value.trim().toLowerCase().replace(/\s+/g, '_')

// Card paid through a wallet, e.g. 'visa_applepay' -> 'Visa (Apple Pay)'
const WALLET_LABELS = {
    applepay: 'Apple Pay',
    googlepay: 'Google Pay',
    mobilepay: 'MobilePay',
}

// Codes that don't identify a method the customer would recognise ('scheme' is a card with
// an unknown brand), so the payment method is hidden instead.
const HIDDEN_PAYMENT_METHODS = ['scheme']

/**
 * Returns a display label for an OMS payment method code (OrderSummary.PaymentMethod__c).
 * @param {string} rawName - Adyen payment method code, e.g. 'klarna', 'mc' or 'visa_applepay'.
 * @param {Object} [paymentMethodLabels] - Code -> label map from the marketless
 * 'paymentMethodLabels' custom preference. Keys are matched lowercase with spaces replaced by
 * underscores (OMS has both 'mc' and 'MC', 'eftpos_australia' and 'eftpos Australia').
 * @returns {string|null} The label, or null when the payment method should not be shown.
 */
export const getPaymentMethodLabel = (rawName, paymentMethodLabels) => {
    if (typeof rawName !== 'string' || !rawName.trim()) return null

    const code = normalizePaymentMethodCode(rawName)
    const labels = {}
    Object.entries(
        paymentMethodLabels && typeof paymentMethodLabels === 'object' ? paymentMethodLabels : {},
    ).forEach(([key, label]) => {
        if (typeof label === 'string' && label) labels[normalizePaymentMethodCode(key)] = label
    })

    if (HIDDEN_PAYMENT_METHODS.includes(code)) return null
    if (labels[code]) return labels[code]

    const [, brand, wallet] = code.match(/^(.+)_(applepay|googlepay|mobilepay)$/) || []
    if (brand && labels[brand]) {
        return `${labels[brand]} (${WALLET_LABELS[wallet]})`
    }

    // Unknown code: show it unchanged rather than a wrong method
    return rawName.trim()
}
